import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Alert, Button, Space } from "antd";
import { tokenManager } from "services/client/token-manager";
import { performLogout } from "services/client/utils";
import { getJwtPayload } from "controllers/api";
import { getRolePermissionsAPI } from "feature/roles-permissions/api/permissions-api";
import { getUserDetailsAPI } from "shared/api/shared";

export const PermissionContext = createContext(null);

/** Dispatch this event to trigger refetch (e.g. after login or logout). */
export const PERMISSION_REFETCH_EVENT = "permission-refetch";

/**
 * Global flag: when true, block the UI (show no-access screen) when user has no permissions or load fails.
 * When false, always render the app even if permissionIds is empty or there was an error.
 * Set via: import { setBlockUiWhenNoPermissions } from "providers/PermissionProvider"; setBlockUiWhenNoPermissions(false);
 */
let blockUiWhenNoPermissions = true;

export function setBlockUiWhenNoPermissions(value) {
  blockUiWhenNoPermissions = Boolean(value);
}

export function getBlockUiWhenNoPermissions() {
  return blockUiWhenNoPermissions;
}

/**
 * Resolves agentId from the access token payload (e.g. sub, account_id, id).
 */
function getAgentIdFromToken(accessToken) {
  if (!accessToken) return null;
  try {
    const payload = getJwtPayload(accessToken);
    return payload?.sub ?? payload?.account_id ?? payload?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Resolves default role id from user details (e.g. role_id, roleId, roles[0].id).
 */
function getDefaultRoleId(userDetails) {
  if (!userDetails) return null;
  const roleId =
    userDetails.role_id ??
    userDetails.roleId ??
    userDetails.default_role_id ??
    (Array.isArray(userDetails.roles) && userDetails.roles[0]?.id) ??
    (Array.isArray(userDetails.roles) && userDetails.roles[0]?.role_id) ??
    (Array.isArray(userDetails.role) && userDetails.role[0]) ??
    null;
  return roleId != null ? String(roleId) : null;
}

/**
 * Flow: Login → access token → get agentId from token → getUserDetailsAPI(agentId) → get default role id → getRolePermissionsAPI(roleId) → store in context.
 * If any API throws, we block the UI and show the no-access screen with logout option.
 */
export function PermissionProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissionIds, setPermissionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const access = tokenManager.getAccess();
    if (!access) {
      setUser(null);
      setPermissionIds([]);
      setLoading(false);
      setError(null);
      return;
    }

    const agentId = getAgentIdFromToken(access);
    if (!agentId) {
      setUser(null);
      setPermissionIds([]);
      setLoading(false);
      setError("Could not resolve user from token");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const userResponse = await getUserDetailsAPI(agentId);
      const raw =
        userResponse?.data?.results ??
        userResponse?.data?.data ??
        userResponse?.data ??
        userResponse;
      if (!raw || typeof raw !== "object") {
        throw new Error("Invalid user details response");
      }
      const userData = { ...raw };
      setUser(userData);

      const roleId = getDefaultRoleId(userData);
      let ids = [];
      if (roleId) {
        const result = await getRolePermissionsAPI(roleId);
        const data = result?.data?.data ?? result?.data ?? result;
        const permissions = data?.permissions;
        // permissions: array of { id, name } e.g. [{ id: "uuid", name: "driver.list.view.route.driver-list" }]
        ids = Array.isArray(permissions) ? permissions.map((p) => String(p?.id ?? p)) : [];
      }
      setPermissionIds(ids);
    } catch (err) {
      setUser(null);
      setPermissionIds([]);
      setError(err?.message || err?.data?.message || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener(PERMISSION_REFETCH_EVENT, handler);
    return () => window.removeEventListener(PERMISSION_REFETCH_EVENT, handler);
  }, [load]);

  const hasToken = !!tokenManager.getAccess();
  const wouldHaveNoAccess = !loading && hasToken && (!!error || permissionIds.length === 0);
  const hasNoAccess = blockUiWhenNoPermissions && wouldHaveNoAccess;

  const value = {
    user,
    permissionIds,
    permissions: permissionIds,
    loading,
    error,
    refetch: load,
    /** True when user is logged in but has no permissions (or permissions failed). Nothing to show in app. */
    hasNoAccess,
  };

  if (loading && hasToken) {
    return (
      <PermissionContext.Provider value={value}>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fafafa",
          }}
        >
          <div style={{ textAlign: "center", color: "#8c8c8c" }}>Loading…</div>
        </div>
      </PermissionContext.Provider>
    );
  }

  if (hasNoAccess) {
    return (
      <PermissionContext.Provider value={value}>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "#fafafa",
          }}
        >
          <div style={{ maxWidth: 480, width: "100%" }}>
            <Alert
              type="warning"
              showIcon
              message="No access"
              description={
                error
                  ? "We could not load your permissions. Please try again or contact your administrator."
                  : "You do not have permission to access any features. Please contact your administrator to get access."
              }
              action={
                <Space>
                  <Button size="small" onClick={load}>
                    Retry
                  </Button>
                  <Button size="small" type="primary" onClick={() => performLogout()}>
                    Logout
                  </Button>
                </Space>
              }
              style={{ marginBottom: 16 }}
            />
            <p style={{ marginTop: 8, fontSize: 13, color: "#8c8c8c" }}>
              You can logout and sign in again with a user that has the required access.
            </p>
          </div>
        </div>
      </PermissionContext.Provider>
    );
  }

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissionContext() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermissionContext must be used within PermissionProvider");
  }
  return ctx;
}
